import { Component, EventEmitter, OnInit } from "@angular/core";
import {
    ITCIUserInfo,
    TCI_AGENT_TYPE,
    TCITenantSelectorType,
    TCITenantAppType,
    TCITenantEnvToolsAgents,
    ITCITenantComponentOptions,
    TCITenantEnvToolsAgentsCreate,
    TCI_AGENT_CREATE_TYPE
} from "@tci/common";
import { NgEnvVarsService } from "../../ng-shared/ng-services/ng-env-vars.service";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { NgUserService } from "../../ng-shared/ng-services/ng-user.service";
import { ITCICEnvToolsComponentOutput, ITCIEnvToolsComponentInput, ITCIEnvToolsAgentCreateMenuOutput } from "../../ng-tenant/tci-tenant-components";
import { AbstractTCITenantComponent, InlineMenuItemHandlerFunction } from "../../ng-tenant/tci-tenant-component";
import { NgEnvToolsComponentInput } from "../tool-loader/ng-env-tools-loader.component";
import { TCITenantMenuServices } from "../../ng-tenant/tci-tenant.menu";
import { BaseEnvToolsAgentMenuComponentInput, BaseEnvToolsAgentCreateMenuComponentInput } from "./base-agent-menu";
import { NgAppFeatureToggleService } from "../../ng-shared/ng-services/ng-app-feature-toggle.service";
import { TCITenantRemoteServices } from "../../ng-tenant/tci-tenant-remote-services";
import { Observable } from "rxjs";
import * as lodash from "lodash";

interface IAgentFilterOption {
    name: string;
    filterType: TCI_AGENT_TYPE;
    iconUrl: string;
}
interface IStoredFilter {
    category: TCI_AGENT_TYPE;
}
const AGENT_SESSION_ITEM = "TCI-AGENT_SESSION_ITEM";

@Component({
    selector: TCITenantSelectorType.TCI_ENV_TOOLS_AGENTS.toString(),
    templateUrl: "./base-agent.component.html",
    // Note: multiple css file in styleUrls MUST keep it in one line, otherwise sourcemap will be in disorder when debug in browser.
    styleUrls: [ "../ng-env-tools.component.css", "./base-agent.component.css" ]
})
export class BaseAgentComponent
    extends AbstractTCITenantComponent<EventEmitter<string>, ITCIEnvToolsComponentInput, ITCICEnvToolsComponentOutput<any>>
    implements OnInit {

    private user: ITCIUserInfo;
    private agentListInput: NgEnvToolsComponentInput;
    isLoading = false;
    public isReadonly = true;
    public selectedFilterType: TCI_AGENT_TYPE;
    public FILTER_CATEGORIES: IAgentFilterOption[] = [];
    public remoteServices: string[] = [];

    // Create agent Menu
    showCreateMenu = false;
    createMenuOptions: TCITenantEnvToolsAgentsCreate[] = [];
    createMenuSelectedOption: TCITenantEnvToolsAgentsCreate;
    createMenuCmpInput: BaseEnvToolsAgentCreateMenuComponentInput;
    showCreateAgentModal = false;
    private tenantCreateAgentOptionsInlineFns: { [key: string]: InlineMenuItemHandlerFunction<any, any, any> } = {};

    /**
     *
     */
    constructor(
        private toastr: NgToastrService,
        private envVarsService: NgEnvVarsService,
        private userService: NgUserService,
        private tenantMenuSrv: TCITenantMenuServices,
        private featureToggleSvc: NgAppFeatureToggleService,
        private tenantRemoteServices: TCITenantRemoteServices,
    ) {
        super();
    }

    ngOnInit(): void {
        this.isLoading = true;
        this.tenantRemoteServices.getRemoteServices()
            .map(remoteSrv => remoteSrv.map(srv => TCITenantAppType.fromString(srv.name).toString()))
            .subscribe(remoteSrv => {
                this.remoteServices = remoteSrv;
                this.buildFilterMenuItems();
                this.loadAgentMenuItems();
            });
    }

    /* Build Side bar filter menu options from menu service. */
    buildFilterMenuItems() {
        const obsCollection: Observable<ITCITenantComponentOptions<TCI_AGENT_TYPE>[]>[] = [];

        TCITenantEnvToolsAgents.getAllMenuItems()
            .filter(comp => comp.getTenantTypes().filter(type => this.remoteServices.includes(type.toString())).length > 0)
            .forEach(menuItem => {
                const input = new BaseEnvToolsAgentMenuComponentInput(this.userService, this.featureToggleSvc, menuItem);
                obsCollection.push(this.tenantMenuSrv.getEnvToolsAgentMenuItems(menuItem, input));
            });

        Observable.merge(...obsCollection)
            .toArray()
            .map(res => [].concat.apply([], res)) // flatten array
            .filter(res => !!res)
            .subscribe((options: TCITenantEnvToolsAgents[]) => {
                options.forEach((option: TCITenantEnvToolsAgents) => {
                    const filterItem: IAgentFilterOption = {
                        name: option.getLabel(),
                        filterType: option.getOptionType(),
                        iconUrl: ""
                    };
                    this.FILTER_CATEGORIES.push(filterItem);
                });
                this.initAgentType();
                this.isLoading = false;
            }, err => {
                console.log(`Error loading env tools agents menu items: ${err}`);
                this.isLoading = false;
            });
    }

    /* Default agent list component to load */
    initAgentType() {
        const isConnectFilterExist = this.FILTER_CATEGORIES.some(filter => filter.filterType === TCI_AGENT_TYPE.AGENT_TYPE_CONNECT);
        const initAgentType = isConnectFilterExist ?
            TCITenantEnvToolsAgents.TCI_ENV_TOOLS_AGENT_CONNECT :
            TCITenantEnvToolsAgents.TCI_ENV_TOOLS_AGENT_HYBRID;

        const categoryFromSession = this.getStoredFilterStage() || "";
        /**
         * If current filter catgory array doesn't contains the filter saved in the session storage,
         * that means teanant state changed since last filter selection.
         */
        const filterPOS = this.FILTER_CATEGORIES.findIndex(cat => cat.name === categoryFromSession);

        this.selectedFilterType = (categoryFromSession && filterPOS > -1) ? categoryFromSession : initAgentType.getLabel();
        this.openAgentType(this.selectedFilterType);
    }

    loadAgentMenuItems() {
        this.buildAgentMenuItems()
            .subscribe(
                res => {
                    this.createMenuOptions = res;
                    this.buildInlineAgentMenuItems();
                },
                err => console.log(`Error loading create agent menu items: ${err}`)
            );
    }

    buildAgentMenuItems() {
        const obsCollection: Observable<ITCITenantComponentOptions<TCI_AGENT_CREATE_TYPE>[]>[] = [];

        TCITenantEnvToolsAgentsCreate.getAllMenuItems()
            .filter(comp => comp.getTenantTypes().filter(type => this.remoteServices.includes(type.toString())).length > 0)
            .forEach(menuItem => {
                const optionType: TCI_AGENT_CREATE_TYPE = menuItem.getOptionType();

                const agentType = (optionType === TCI_AGENT_CREATE_TYPE.AGENT_CREATE_TYPE_HYBRID || optionType === TCI_AGENT_CREATE_TYPE.AGENT_CREATE_TYPE_UNIFIED_MGMT) ?
                    TCITenantEnvToolsAgents.TCI_ENV_TOOLS_AGENT_HYBRID : TCITenantEnvToolsAgents.TCI_ENV_TOOLS_AGENT_CONNECT;

                const input = new BaseEnvToolsAgentCreateMenuComponentInput(
                    this.userService,
                    this.envVarsService,
                    this.featureToggleSvc,
                    agentType,
                    menuItem);

                obsCollection.push(this.tenantMenuSrv.getEnvToolsAgentCreateMenuItems(menuItem, input));
            });

        return Observable.concat(...obsCollection)
            .toArray()
            .map(res => [].concat.apply([], res)) // flatten array
            .filter(res => !!res)
            .map((res: TCITenantEnvToolsAgentsCreate[]) => (res && res.length > 0) ? res : []);
    }

    buildInlineAgentMenuItems() {
        if (this.createMenuOptions && this.createMenuOptions.length > 0) {
            this.createMenuOptions
                .forEach(opt => {
                    const selector = opt.getComponentSelectorType().toString();
                    opt.getTenantTypes().forEach(tenant => {
                        const preloadMap = this.tenantMenuSrv.getTenantPreloadMap(tenant);
                        if (preloadMap && Object.keys(preloadMap).length > 0) {
                            this.tenantCreateAgentOptionsInlineFns[selector] = preloadMap[selector] &&
                                preloadMap[selector].inlineMenuHandlerFn;
                        }
                    });
                });
        }
    }

    executeInlineCompFn($event: MouseEvent, opt: TCITenantEnvToolsAgentsCreate) {
        const selector = opt.getComponentSelectorType().toString();
        const optionType: TCI_AGENT_CREATE_TYPE = opt.getOptionType();

        const agentType = (optionType === TCI_AGENT_CREATE_TYPE.AGENT_CREATE_TYPE_HYBRID || optionType === TCI_AGENT_CREATE_TYPE.AGENT_CREATE_TYPE_UNIFIED_MGMT) ?
            TCITenantEnvToolsAgents.TCI_ENV_TOOLS_AGENT_HYBRID :
            TCITenantEnvToolsAgents.TCI_ENV_TOOLS_AGENT_CONNECT;

        const tenantAgentCreateInput = new BaseEnvToolsAgentCreateMenuComponentInput(
            this.userService,
            this.envVarsService,
            this.featureToggleSvc,
            agentType,
            opt
        );
        opt.getTenantTypes().forEach(tenant => {
            const injector = this.tenantMenuSrv.getTenantInjector(tenant);
            this.tenantCreateAgentOptionsInlineFns[selector](opt, tenantAgentCreateInput, injector)
                .subscribe((response: ITCIEnvToolsAgentCreateMenuOutput) => {
                    this.evaluateCreateMenuOutput(response);
                });
        });
    }

    mountCreateAgentComp(menuItem: TCITenantEnvToolsAgentsCreate) {
        const optionType = menuItem.getOptionType();
        const agentType = (optionType === TCI_AGENT_CREATE_TYPE.AGENT_CREATE_TYPE_HYBRID || optionType === TCI_AGENT_CREATE_TYPE.AGENT_CREATE_TYPE_UNIFIED_MGMT) ?
            TCITenantEnvToolsAgents.TCI_ENV_TOOLS_AGENT_HYBRID : TCITenantEnvToolsAgents.TCI_ENV_TOOLS_AGENT_CONNECT;

        this.createMenuCmpInput = new BaseEnvToolsAgentCreateMenuComponentInput(
            this.userService,
            this.envVarsService,
            this.featureToggleSvc,
            agentType,
            menuItem
        );
        this.createMenuSelectedOption = menuItem;
        this.showCreateAgentModal = true;
        this.toggleCreateMenu();
    }

    toggleCreateMenu() {
        this.showCreateMenu = !this.showCreateMenu;
    }

    evaluateCreateMenuOutput(e: ITCIEnvToolsAgentCreateMenuOutput) {
        if (e) {
            this.showCreateMenu = false;
            // Take action only when requested.
            if (e.closeModal) {
                this.showCreateAgentModal = false;
            }
            if (e.refresh) {
                this.loadAgentMenuItems();
            }
        } else {
            console.log("Evaluate agent create menu output: No event recieved");
        }
    }

    createMenuCmpClose() {
        this.showCreateAgentModal = false;
    }

    openAgentType(filterType: TCI_AGENT_TYPE) {
        // load the selected Agent type component
        this.selectedFilterType = filterType;

        if (this.selectedFilterType === TCI_AGENT_TYPE.AGENT_TYPE_CONNECT) {
            this.mountAgentList(TCITenantEnvToolsAgents.TCI_ENV_TOOLS_AGENT_CONNECT);
        } else if (this.selectedFilterType === TCI_AGENT_TYPE.AGENT_TYPE_HYBRID) {
            this.mountAgentList(TCITenantEnvToolsAgents.TCI_ENV_TOOLS_AGENT_HYBRID);
        }
        this.storeFilterStage();
    }

    storeFilterStage() {
        const filters: IStoredFilter = { category: this.selectedFilterType };
        sessionStorage.setItem(AGENT_SESSION_ITEM, JSON.stringify(filters));
    }

    getStoredFilterStage(): TCI_AGENT_TYPE {
        let storedFilter: TCI_AGENT_TYPE;
        const filterStageData = sessionStorage.getItem(AGENT_SESSION_ITEM);
        if (!lodash.isEmpty(filterStageData)) {
            try {
                storedFilter = JSON.parse(filterStageData).category;
            } catch (e) {
                console.error("Agents stage restore incomplete", e);
            }
        }
        return storedFilter;
    }

    mountAgentList(type: TCITenantEnvToolsAgents) {
        const tenantType = TCITenantAppType.createPseudoTenant(...TCITenantAppType.getAllTypes());
        this.agentListInput = new NgEnvToolsComponentInput(tenantType, this.userService, this.featureToggleSvc, type);
    }
}
